/*============================================================================
  Cart-Level Validation: Block checkout when required APO options are missing.

  PURPOSE:
  Catches products added via Buy Again, Shop app, direct URL, or any other
  route that bypasses the product page where APO validation normally runs.

  HOW IT WORKS:
  - Liquid (cart-item.liquid) adds data-missing-options="true" on cart items
    that need APO options but don't have them.
  - This script finds those items and:
    1. Shows error messages above the checkout button
    2. Blocks form submission on checkout click
    3. Hides PayPal / Shop Pay / Apple Pay buttons via CSS class
    4. Re-checks after every AJAX cart rebuild (quantity change, item removal)
    5. Auto-removes the incomplete item from cart when "Select options" is clicked

  FILES INVOLVED:
  - snippets/cart-item.liquid   — detection logic + data attributes
  - sections/main-cart.liquid   — error container + CSS styles
  - snippets/cart-drawer.liquid — error container
  - assets/cart-validation.js   — this file (all JS logic)
==============================================================================*/

(function () {
  'use strict';

  // ---- Helpers ----

  /**
   * Scan a cart form for items with data-missing-options="true"
   * and show/hide the error message + blocking class accordingly.
   */
  function checkMissingOptions(form) {
    var missingItems = form.querySelectorAll('[data-missing-options="true"]');
    var errorContainer = form.querySelector('.cart__validation-errors');

    if (missingItems.length > 0) {
      form.classList.add('cart--has-missing-options');

      if (errorContainer) {
        var errorHTML =
          '<p>\u26A0 Some items are missing required selections and cannot be checked out:</p><ul>';
        missingItems.forEach(function (item) {
          var title = item.getAttribute('data-product-title') || 'Unknown Product';
          var url = item.getAttribute('data-product-url') || '#';
          errorHTML +=
            '<li><strong>' + title + '</strong> \u2014 required options not selected</li>';
        });
        errorHTML += '</ul>';
        errorContainer.innerHTML = errorHTML;
        errorContainer.style.display = 'block';
      }
    } else {
      form.classList.remove('cart--has-missing-options');
      if (errorContainer) {
        errorContainer.style.display = 'none';
        errorContainer.innerHTML = '';
      }
    }
  }

  /**
   * Remove item from cart via Shopify AJAX API, then redirect to product page.
   */
  function removeItemAndRedirect(itemKey, productUrl) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: itemKey, quantity: 0 })
    })
    .then(function () {
      // Redirect to product page so customer can select options properly
      window.location.href = productUrl;
    })
    .catch(function () {
      // If removal fails, just redirect anyway
      window.location.href = productUrl;
    });
  }

  /**
   * Block form submission if any cart item is missing required options.
   * Runs BEFORE theme.js onSubmit because we use the capture phase.
   */
  function onFormSubmit(evt) {
    var form = evt.currentTarget;
    var missingItems = form.querySelectorAll('[data-missing-options="true"]');

    if (missingItems.length === 0) return; // all good, let theme.js handle it

    evt.preventDefault();
    evt.stopImmediatePropagation(); // prevent theme.js onSubmit from firing

    // Remove loading state from checkout button
    var submitBtn = form.querySelector('.cart__checkout');
    if (submitBtn) {
      submitBtn.classList.remove('btn--loading');
    }

    // Show error
    var errorContainer = form.querySelector('.cart__validation-errors');
    if (errorContainer) {
      var errorHTML =
        '<p>\u26A0 Some items are missing required selections and cannot be checked out:</p><ul>';
      missingItems.forEach(function (item) {
        var title = item.getAttribute('data-product-title') || 'Unknown Product';
        var url = item.getAttribute('data-product-url') || '#';
        errorHTML +=
          '<li><strong>' + title + '</strong> \u2014 required options not selected' +
          ' &nbsp;<a href="' + url + '" class="cart__error-link">\u2192 Select options</a></li>';
      });
      errorHTML += '</ul>';
      errorContainer.innerHTML = errorHTML;
      errorContainer.style.display = 'block';

      // Scroll to error on cart page
      var isPage = form.getAttribute('data-location') === 'page';
      if (isPage) {
        errorContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } else {
      // Fallback alert
      var titles = [];
      missingItems.forEach(function (item) {
        titles.push(item.getAttribute('data-product-title') || 'Unknown');
      });
      alert(
        'The following items are missing required options: ' + titles.join(', ') +
        '\n\nPlease visit the product page to select your required options before checking out.'
      );
    }

    form.classList.add('cart--has-missing-options');
  }

  /**
   * Handle clicks on "Select options" links — remove item from cart
   * then redirect to the product page.
   */
  function onSelectOptionsClick(evt) {
    var link = evt.target.closest('[data-remove-key]');
    if (!link) return;

    evt.preventDefault();

    var itemKey = link.getAttribute('data-remove-key');
    var productUrl = link.getAttribute('href');

    // Show a brief loading state on the link
    link.textContent = 'Redirecting...';
    link.style.pointerEvents = 'none';

    removeItemAndRedirect(itemKey, productUrl);
  }

  // ---- Initialization ----

  /**
   * Set up validation on a single cart form (page or drawer).
   */
  function initForm(form) {
    // Proactive check on load
    checkMissingOptions(form);

    // Block submit — capture phase runs BEFORE theme.js event handler
    form.addEventListener('submit', onFormSubmit, true);

    // Listen for "Select options" clicks to auto-remove item + redirect
    form.addEventListener('click', onSelectOptionsClick);

    // Watch for AJAX cart rebuilds (theme.js replaces [data-products] innerHTML)
    var productsContainer = form.querySelector('[data-products]');
    if (productsContainer) {
      var observer = new MutationObserver(function () {
        checkMissingOptions(form);
      });
      observer.observe(productsContainer, { childList: true, subtree: true });
    }
  }

  /**
   * Find and init all cart forms on the page.
   */
  function init() {
    // Cart page form
    var pageForm = document.getElementById('CartPageForm');
    if (pageForm) initForm(pageForm);

    // Cart drawer form
    var drawerForm = document.getElementById('CartDrawerForm');
    if (drawerForm) initForm(drawerForm);
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();


/*============================================================================
  APO line pairing: keep main products and their add-on lines in sync.

  LINK FORMAT (from /cart.js on this store):
  - Add-on line: properties._mws_option   = "o_2060777"
  - Main line:   properties._mws_products = "o_2060777,53851741356346,1"

  BEHAVIOUR:
  - Main product removed -> its own add-on lines are removed
  - Add-on removed       -> its main product is removed once none of its
                            add-ons remain

  Each main line is judged only against the o_ ids listed in its own
  _mws_products value, so two different products with add-ons cannot cover
  for each other.

  We do the removal ourselves rather than waiting for the app, then fire
  cart:build so the drawer and cart page re-render without a refresh.

  NOTE: theme.cart.changeItem does not dispatch cart:updated, so the cleanup
  cannot trigger itself. The isCleaning flag guards against overlap anyway.
==============================================================================*/

(function () {
  'use strict';

  var DEBUG = false;
  var isCleaning = false;

  function log() {
    if (DEBUG) console.log.apply(console, ['[apo-sync]'].concat([].slice.call(arguments)));
  }

  function optionId(item) {
    return item.properties && item.properties._mws_option;
  }

  function productsRef(item) {
    return (item.properties && item.properties._mws_products) || '';
  }

  function optionIdsIn(ref) {
    return ref.split(/[,;\s]+/).filter(function (part) {
      return part.indexOf('o_') === 0;
    });
  }

  function cleanup() {
    if (isCleaning) return;

    theme.cart.getCart().then(function (cart) {
      var mains = [];
      var addonsByOption = {};

      cart.items.forEach(function (item) {
        var opt = optionId(item);

        if (opt) {
          if (!addonsByOption[opt]) addonsByOption[opt] = [];
          addonsByOption[opt].push(item);
          return;
        }

        var ids = optionIdsIn(productsRef(item));
        if (ids.length) mains.push({ item: item, ids: ids });
      });

      // Every o_ id claimed by a main line that is still in the cart
      var claimed = {};
      mains.forEach(function (main) {
        main.ids.forEach(function (id) {
          claimed[id] = true;
        });
      });

      var orphans = [];

      // Add-on lines that no surviving main line claims
      Object.keys(addonsByOption).forEach(function (id) {
        if (!claimed[id]) {
          addonsByOption[id].forEach(function (item) {
            orphans.push(item.key);
          });
        }
      });

      // Main lines whose own add-ons have all gone
      mains.forEach(function (main) {
        var anyLeft = main.ids.some(function (id) {
          return addonsByOption[id] && addonsByOption[id].length;
        });
        if (!anyLeft) orphans.push(main.item.key);
      });

      log(
        'mains', mains.length,
        'addons', Object.keys(addonsByOption),
        'orphans', orphans
      );

      if (!orphans.length) return;

      isCleaning = true;

      // Sequential, not parallel. Shopify rejects concurrent cart writes.
      var chain = Promise.resolve();

      orphans.forEach(function (key) {
        chain = chain.then(function () {
          return theme.cart.changeItem(key, 0);
        });
      });

      chain
        .then(function () {
          log('removed, rebuilding');
          document.dispatchEvent(new CustomEvent('cart:build'));
        })
        .catch(function (error) {
          console.error('[apo-sync] cleanup failed', error);
        })
        .then(function () {
          isCleaning = false;
        });
    }).catch(function () {});
  }

  function schedule() {
    setTimeout(cleanup, 400);
    setTimeout(cleanup, 1200);
  }

  function init() {
    log('init');

    // Catch anything already broken before this page load
    cleanup();

    // Any interaction inside a cart form could change the cart
    document.addEventListener('click', function (evt) {
      if (evt.target.closest('#CartDrawerForm, #CartPageForm')) schedule();
    });

    document.addEventListener('cart:updated', schedule);
    document.addEventListener('ajaxProduct:added', schedule);
  }

  // theme.js is deferred and loads before this file, so theme.cart already
  // exists and page:loaded has already fired by the time this runs.
  if (window.theme && theme.cart) {
    init();
  } else {
    document.addEventListener('page:loaded', init);
  }
})();